import * as ContextMenu from "@radix-ui/react-context-menu";
import { Archive, CheckCircle2, Circle, ExternalLink } from "lucide-react";

interface Props {
  children: React.ReactNode;
  notification: {
    id: string;
    isRead: boolean;
    isArchived: boolean;
    relatedEntityId?: string;
    relatedEntityType?: string;
  };
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  onOpenRelated: (id: string) => void;
}

export function InboxContextMenu({
  children,
  notification,
  onArchive,
  onUnarchive,
  onMarkRead,
  onMarkUnread,
  onOpenRelated,
}: Props) {
  const isArchived = notification.isArchived;

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>

      <ContextMenu.Portal>
        <ContextMenu.Content
          className="
            z-50 min-w-[200px] overflow-hidden rounded-lg border border-border
            bg-card shadow-lg shadow-black/10
            p-1 text-[13px]
            animate-in fade-in-0 zoom-in-95 duration-100
          "
        >
          {/* Read/Unread */}
          <ContextMenu.Item
            onSelect={() =>
              notification.isRead
                ? onMarkUnread(notification.id)
                : onMarkRead(notification.id)
            }
            className="
              flex items-center justify-between gap-6 px-2.5 py-1.5 rounded-md
              cursor-default select-none outline-none
              text-primary hover:bg-accent-soft hover:text-accent
              transition-colors
            "
          >
            <span className="flex items-center gap-2">
              {notification.isRead ? (
                <Circle size={13} className="text-muted" />
              ) : (
                <CheckCircle2 size={13} className="text-muted" />
              )}
              {notification.isRead ? "Mark as unread" : "Mark as read"}
            </span>
            <kbd className="text-[11px] text-muted font-mono">U</kbd>
          </ContextMenu.Item>

          {/* Archive / Unarchive */}
          <ContextMenu.Item
            onSelect={() =>
              isArchived
                ? onUnarchive(notification.id)
                : onArchive(notification.id)
            }
            className="
              flex items-center justify-between gap-6 px-2.5 py-1.5 rounded-md
              cursor-default select-none outline-none
              text-primary hover:bg-accent-soft hover:text-accent
              transition-colors
            "
          >
            <span className="flex items-center gap-2">
              <Archive size={13} className="text-muted" />
              {isArchived ? "Move to Inbox" : "Archive"}
            </span>
            {!isArchived && (
              <kbd className="text-[11px] text-muted font-mono">E</kbd>
            )}
          </ContextMenu.Item>

          {/* Open related entity */}
          {notification.relatedEntityId && (
            <>
              <ContextMenu.Separator className="my-1 h-px bg-border" />
              <ContextMenu.Item
                onSelect={() => onOpenRelated(notification.id)}
                className="
                  flex items-center gap-2 px-2.5 py-1.5 rounded-md
                  cursor-default select-none outline-none
                  text-primary hover:bg-accent-soft hover:text-accent
                  transition-colors
                "
              >
                <ExternalLink size={13} className="text-muted" />
                Open{" "}
                {notification.relatedEntityType === "document"
                  ? "Document"
                  : "Audit"}
              </ContextMenu.Item>
            </>
          )}
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
